import { useCallback, useEffect, useMemo, useRef } from "react";
import styles from "./style.module.scss";
import { useLabelPropagation } from "hooks/LabelPropagation/useLabelPropagation";
import { Option } from "views/uikit/FormFields/SelectList";
import { useDispatch } from "react-redux";
import VLButton from "views/uikit/Buttons/VLButton";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import VLActionModal from "views/uikit/VLModal/VLActionModal";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";
import { useSearchParams } from "react-router-dom";
import {
  ENTITY_TYPE_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  SEED_PARAMETER,
} from "helpers/constants/miscellaneous";
import VLCount from "views/uikit/VLCount";
import {
  fetchDataset,
  fetchDatasetLabels,
  fetchFilterConfig,
  fetchMetadataSources,
  fetchSimilarityData,
} from "redux/SingleDataset/actions";
import {
  DatasetStatusEnum,
  EDatasetStatus,
  EntityType,
  isValidEntityType,
} from "types";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActiveQueryByOperationId } from "../../../vl-api/vlUtils";
import { FlywheelStatus } from "../../../vl-api/vlSchemas";
import DisabledLabelPropagation from "./DisabledLabelPropagation";
import { useSeed } from "../../../hooks/LabelPropagation/useSeed";
import { useFeatureFlagService } from "../../../hooks/useFeatureFlagService";
import { useUserPermission } from "../../../hooks/useUserPermission";
import { NO_PERMISSION_TOOLTIP_TEXT } from "../../../helpers/constants/tooltips";
import useAllQueryParamsFilters from "../../../hooks/useAllQueryParamsFilters";
import { useNavigate } from "react-router";
import { Guide, GuideButton, useAutoOpenGuide } from "views/components/Guide";
import {
  useLpReviewGuideStore,
  useLpSeedGuideStore,
} from "./guides/lpGuideStores";
import { LP_SEED_GUIDE_STEPS } from "./guides/lpSeedGuide";
import { LP_REVIEW_GUIDE_STEPS } from "./guides/lpReviewGuide";

interface LabelPropagationProps {
  datasetId: string;
  onDataChange: (data: {
    classesOptions: Option[];
    displayNewFloatingPanel: boolean;
  }) => void;
  assignItemsToClass?: string;
  onAssign?: (assignFunc: (className: string) => void) => void;
  initialStep?: number;
  resetShouldFetch: () => void;
  shouldFetch?: string;
  flywheelStatus?: FlywheelStatus;
  showSummaryTab: () => void;
  deprecatedDatasetStatus?: DatasetStatusEnum;
  datasetStatus?: EDatasetStatus;
}

export default function LabelPropagation({
  datasetId,
  deprecatedDatasetStatus,
  datasetStatus,
  showSummaryTab,
  ...rest
}: LabelPropagationProps) {
  const [searchParams] = useSearchParams();
  const { loading } = useSeed(datasetId, showSummaryTab);
  const LabelPropagationBehavior = useFeatureFlagService("FLYWHEEL");
  const canUseFlywheel = useUserPermission("can_use_flywheel");

  if (!LabelPropagationBehavior.show) {
    return (
      <div className={styles.notEnabled}>
        This feature isn’t available right now.
      </div>
    );
  }

  if (!canUseFlywheel) {
    return (
      <div className={styles.notEnabled}>{NO_PERMISSION_TOOLTIP_TEXT}</div>
    );
  }

  if (
    deprecatedDatasetStatus &&
    datasetStatus &&
    !LabelPropagationBehavior.enabled
  ) {
    return (
      <DisabledLabelPropagation
        deprecatedDatasetStatus={deprecatedDatasetStatus}
        datasetStatus={datasetStatus}
        datasetId={datasetId}
      />
    );
  }

  if (loading || !searchParams.has(SEED_PARAMETER)) {
    return (
      <div className={styles.loaderWrapper}>
        <VLColorfulLoader />
      </div>
    );
  }

  return (
    <Content datasetId={datasetId} showSummaryTab={showSummaryTab} {...rest} />
  );
}

const Content = ({
  datasetId,
  onDataChange,
  onAssign,
  shouldFetch,
  resetShouldFetch,
  flywheelStatus,
  showSummaryTab,
}: Omit<LabelPropagationProps, "datasetStatus">) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const entityParam = searchParams.get(ENTITY_TYPE_PARAMETER);
  const entityTypeValue: EntityType = isValidEntityType(entityParam)
    ? entityParam
    : "IMAGES";

  const queryClient = useQueryClient();

  const queryParamsFilters = useAllQueryParamsFilters();
  const navigate = useNavigate();

  const onLabelPropagationCompleted = useCallback(() => {
    dispatch(fetchDatasetLabels(datasetId, entityTypeValue) as any);
    dispatch(fetchMetadataSources(datasetId, entityTypeValue) as any);
    dispatch(fetchFilterConfig(datasetId) as any);
    dispatch(fetchDataset(datasetId) as any);
    invalidateActiveQueryByOperationId(
      queryClient,
      "flywheelDatasetCurrentStatusApiV1FlywheelDatasetIdGet",
    );
    showSummaryTab();
    dispatch(fetchSimilarityData(datasetId, queryParamsFilters) as any);
    navigate(`/dataset/${datasetId}/data`);
  }, [
    navigate,
    queryParamsFilters,
    dispatch,
    datasetId,
    entityTypeValue,
    queryClient,
    showSummaryTab,
  ]);

  const {
    loading,
    currentStep,
    step,
    selectedLabels,
    classesOptions,
    assignItemsToClassHandler,
    isLoading,
    modal,
  } = useLabelPropagation(
    datasetId,
    onLabelPropagationCompleted,
    resetShouldFetch,
    showSummaryTab,
    flywheelStatus,
    shouldFetch || undefined,
  );

  const inReview = searchParams.has(REVIEW_PARAMETER);
  const labelId = searchParams.has(LABEL_CLASS_PARAMETER);

  const isSeedStep = currentStep?.key === 1;
  const seedGuideOpen = useLpSeedGuideStore((s) => s.open);
  const openSeedGuide = useLpSeedGuideStore((s) => s.openGuide);
  const closeSeedGuide = useLpSeedGuideStore((s) => s.closeGuide);
  const reviewGuideOpen = useLpReviewGuideStore((s) => s.open);
  const openReviewGuide = useLpReviewGuideStore((s) => s.openGuide);
  const closeReviewGuide = useLpReviewGuideStore((s) => s.closeGuide);
  useAutoOpenGuide(useLpSeedGuideStore, !loading && isSeedStep);
  useAutoOpenGuide(
    useLpReviewGuideStore,
    !loading && flywheelStatus === "PAUSED_FOR_REVIEW",
  );
  const guideButton = (
    <GuideButton
      tooltip={
        isSeedStep ? "How Label Propagation works" : "How reviewing works"
      }
      onClick={isSeedStep ? openSeedGuide : openReviewGuide}
    />
  );

  // store previous values to prevent unnecessary onDataChange calls
  const prevDataRef = useRef<{ values: string[]; displayPanel: boolean }>();
  const stepContentRef = useRef<HTMLDivElement>(null);

  // memoize selected options and values to prevent new references each render
  const selectedOptions = useMemo(
    () => classesOptions.filter((opt) => selectedLabels?.includes(opt.value)),
    [classesOptions, selectedLabels],
  );

  const selectedValues = useMemo(
    () => selectedOptions.map((opt) => opt.value),
    [selectedOptions],
  );

  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    stepContentRef,
    { current: null },
    { shouldObserveInnerRef: false },
  );

  const isContentOverflowing = isOverflowing && !isAtBottom;

  useEffect(() => {
    if (!onDataChange || !currentStep) return;

    const displayPanel = currentStep.key === 1;
    const prevData = prevDataRef.current;
    const isLabelReview = inReview && labelId;

    const hasChanged =
      !prevData ||
      prevData.displayPanel !== displayPanel ||
      isLabelReview ||
      prevData.values.length !== selectedValues.length ||
      prevData.values.some((v, i) => v !== selectedValues[i]);

    if (hasChanged) {
      prevDataRef.current = {
        values: [...selectedValues],
        displayPanel: displayPanel || isLabelReview,
      };
      onDataChange({
        classesOptions: selectedOptions,
        displayNewFloatingPanel: displayPanel || isLabelReview,
      });
    }
  }, [
    selectedValues,
    selectedOptions,
    onDataChange,
    currentStep,
    inReview,
    labelId,
  ]);
  const assignRef = useRef(assignItemsToClassHandler);
  assignRef.current = assignItemsToClassHandler;
  const handlerRef = useRef<(className: string) => void>();

  useEffect(() => {
    handlerRef.current = (className: string) => {
      assignRef.current?.(className);
    };
  }, []);
  useEffect(() => {
    if (!onAssign) return;
    onAssign(handlerRef.current!);
  }, [onAssign]);

  if (loading || !step) return <div>Loading...</div>;

  const displayButtons = currentStep.nextButton || currentStep.prevButton;

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <VLColorfulLoader />
      </div>
    );
  }
  return (
    <div className={styles.wrapper}>
      {modal && <VLActionModal {...modal} />}
      <Guide
        open={seedGuideOpen}
        onClose={closeSeedGuide}
        steps={LP_SEED_GUIDE_STEPS}
      />
      <Guide
        open={reviewGuideOpen}
        onClose={closeReviewGuide}
        steps={LP_REVIEW_GUIDE_STEPS}
      />
      <div className={styles.content}>
        {isSeedStep && flywheelStatus === "COMPLETED" && (
          <div className={styles.rerunNotice} role="note">
            <strong>This dataset already has propagated labels.</strong> A new
            run replaces all of them. To keep labels, filter by each label and
            drag those images onto its seed card before you start.
          </div>
        )}
        {!currentStep.title && (
          <div className={styles.title}>
            <div className={styles.titleAndButton}>
              <span className={styles.titleText}>Label Propagation</span>
              {guideButton}
            </div>
          </div>
        )}
        {currentStep.title && (
          <>
            <div className={styles.title}>
              <div className={styles.titleAndButton}>
                <span className={styles.titleText}>
                  {currentStep.title.text}
                </span>
                {currentStep.title.icon && currentStep.title.icon}
                {guideButton}
              </div>
              {currentStep?.title?.count !== undefined && (
                <VLCount count={currentStep.title.count} />
              )}
            </div>
            {currentStep.title?.subText && (
              <div className={styles.subTitle}>
                <span className={styles.subTitleText}>
                  {currentStep.title.subText}
                </span>
              </div>
            )}
          </>
        )}
        <div className={styles.stepContent} ref={stepContentRef}>
          {currentStep.content}
        </div>
      </div>

      {isContentOverflowing && <div className={styles.overflow}></div>}

      {displayButtons && step && (
        <div className={styles.buttonsWrapper}>
          <div className={styles.buttons}>
            {step > 1 && currentStep.prevButton && (
              <VLButton
                className={styles.nextButton}
                size="middle"
                danger={true}
                onClick={currentStep.prevButton.onClick}
                disabled={currentStep.prevButton.disabled}
                text={currentStep.prevButton.text}
                type={currentStep.prevButton.type}
                tooltip={currentStep.prevButton.tooltip}
              />
            )}
            {currentStep.nextButton && (
              <VLButton
                className={styles.nextButton}
                size="middle"
                type={"primary"}
                disabled={currentStep.nextButton.disabled}
                onClick={currentStep.nextButton.onClick}
                isGradient={currentStep.nextButton.isGradient}
                data-testid="next-button"
                text={currentStep.nextButton.text}
                tooltip={currentStep.nextButton.tooltip}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
